import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "header", "footer", "isOpen", "onClose", "portalContainer", "bodyBackgroundColor", "bodyClassName", "bodyOverflowY", "bodyPadding", "bodyPaddingS", "bodyPaddingXS", "contentClassName", "fullHeight", "fullScreen", "disableTransition", "stackedDialog"];
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import React, { useEffect, useRef } from 'react';
import { DialogStyles } from "./Dialog.styles.js";
import { useBlockScroll } from "../../hooks/useBlockScroll.js";
export var Dialog = function Dialog(_ref) {
  var children = _ref.children,
    header = _ref.header,
    footer = _ref.footer,
    isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    portalContainer = _ref.portalContainer,
    bodyBackgroundColor = _ref.bodyBackgroundColor,
    bodyClassName = _ref.bodyClassName,
    bodyOverflowY = _ref.bodyOverflowY,
    bodyPadding = _ref.bodyPadding,
    bodyPaddingS = _ref.bodyPaddingS,
    bodyPaddingXS = _ref.bodyPaddingXS,
    contentClassName = _ref.contentClassName,
    fullHeight = _ref.fullHeight,
    fullScreen = _ref.fullScreen,
    disableTransition = _ref.disableTransition,
    _ref$stackedDialog = _ref.stackedDialog,
    stackedDialog = _ref$stackedDialog === void 0 ? false : _ref$stackedDialog,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var ariaLabelFromProps = otherProps['aria-label'];
  var ariaLabelAttr = ariaLabelFromProps !== null && ariaLabelFromProps !== void 0 ? ariaLabelFromProps : typeof (header === null || header === void 0 ? void 0 : header.title) === 'string' ? header.title : undefined;
  var openerRef = useRef(null);
  var wasOpenRef = useRef(false);
  useBlockScroll(isOpen);
  useEffect(function () {
    if (isOpen) {
      if (!wasOpenRef.current) {
        var _ref2;
        openerRef.current = (_ref2 = document.activeElement) !== null && _ref2 !== void 0 ? _ref2 : null;
      }
    } else if (wasOpenRef.current) {
      var _openerRef$current, _openerRef$current$fo;
      (_openerRef$current = openerRef.current) === null || _openerRef$current === void 0 || (_openerRef$current$fo = _openerRef$current.focus) === null || _openerRef$current$fo === void 0 || _openerRef$current$fo.call(_openerRef$current);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);
  var onHeaderClose = function onHeaderClose(event) {
    onClose(event, 'crossButtonClick');
  };
  var onEscapeKeyDown = function onEscapeKeyDown(event) {
    onClose(event, 'escapeKeyDown');
    event.preventDefault();
  };
  var onPointerDownOutside = function onPointerDownOutside(event) {
    // Always prevent Radix from auto-dismissing; this component is controlled by `isOpen`.
    // Backdrop clicks are handled via the overlay element (visible or transparent).
    event.preventDefault();
  };
  var onCloseAutoFocus = function onCloseAutoFocus(event) {
    var _openerRef$current2, _openerRef$current2$f;
    event.preventDefault();
    (_openerRef$current2 = openerRef.current) === null || _openerRef$current2 === void 0 || (_openerRef$current2$f = _openerRef$current2.focus) === null || _openerRef$current2$f === void 0 || _openerRef$current2$f.call(_openerRef$current2);
  };
  var a11yTitle = 'Dialog';
  if (typeof (header === null || header === void 0 ? void 0 : header.title) === 'string' && header.title.length > 0) {
    a11yTitle = header.title;
  } else if (typeof ariaLabelAttr === 'string' && ariaLabelAttr.length > 0) {
    a11yTitle = ariaLabelAttr;
  }
  return /*#__PURE__*/React.createElement(DialogPrimitive.Root, {
    open: isOpen,
    modal: true
  }, /*#__PURE__*/React.createElement(DialogPrimitive.Portal, {
    container: portalContainer !== null && portalContainer !== void 0 ? portalContainer : undefined
  }, stackedDialog ? /*#__PURE__*/React.createElement(DialogStyles.Overlay, {
    "data-testid": "dialog-overlay-transparent",
    $disableTransition: disableTransition,
    $transparent: true,
    onPointerDown: function onPointerDown(event) {
      onClose(event, 'backdropClick');
      event.preventDefault();
    }
  }) : /*#__PURE__*/React.createElement(DialogStyles.Overlay, {
    "data-testid": "dialog-overlay",
    $disableTransition: disableTransition,
    onPointerDown: function onPointerDown(event) {
      onClose(event, 'backdropClick');
      event.preventDefault();
    }
  }), /*#__PURE__*/React.createElement(DialogStyles.Paper, _extends({}, otherProps, {
    "aria-label": ariaLabelAttr,
    $fullScreen: fullScreen,
    $fullHeight: fullHeight || stackedDialog,
    $disableTransition: disableTransition,
    $stickyHeader: !!(header !== null && header !== void 0 && header.sticky),
    $bodyOverflowY: bodyOverflowY,
    onEscapeKeyDown: onEscapeKeyDown,
    onPointerDownOutside: onPointerDownOutside,
    onCloseAutoFocus: onCloseAutoFocus
  }), /*#__PURE__*/React.createElement(DialogPrimitive.Title, {
    asChild: true
  }, /*#__PURE__*/React.createElement(VisuallyHidden.Root, null, a11yTitle)), /*#__PURE__*/React.createElement(DialogPrimitive.Description, {
    asChild: true
  }, /*#__PURE__*/React.createElement(VisuallyHidden.Root, null)), header && /*#__PURE__*/React.createElement(DialogStyles.Header, {
    onClose: onHeaderClose,
    sticky: header.sticky,
    title: header.title,
    element: header.element,
    action: header.action,
    stackedDialog: stackedDialog
  }), /*#__PURE__*/React.createElement(DialogStyles.Body, {
    className: bodyClassName,
    $bodyBackgroundColor: bodyBackgroundColor,
    $bodyOverflowY: bodyOverflowY,
    $stickyHeader: !!(header !== null && header !== void 0 && header.sticky)
  }, /*#__PURE__*/React.createElement(DialogStyles.Content, {
    className: contentClassName,
    $defaultBodyPadding: bodyPadding,
    $bodyPaddingS: bodyPaddingS,
    $bodyPaddingXS: bodyPaddingXS
  }, children), footer && /*#__PURE__*/React.createElement(DialogStyles.Footer, {
    element: footer.element,
    sticky: footer.sticky,
    primaryButton: footer.primaryButton,
    secondaryButton: footer.secondaryButton,
    buttonLayoutModifier: footer.buttonLayoutModifier,
    footerAdditionalElement: footer.footerAdditionalElement,
    bodyPaddingS: footer.bodyPaddingS,
    defaultBodyPadding: footer.defaultBodyPadding,
    className: footer.className
  })))));
};